import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { LeasingvertragContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type LeasingvertragContactFormFieldNameT =
  | "company"
  | "salutation"
  | "firstname"
  | "lastname"
  | "email"
  | "phone"
  | "comments"
  | "tradeIn"
  | "licensePlate"
  | "make"
  | "contactPerson"
  | "makeName"
  | "vehicleLicensePlate"
  | "vehicleVin"
  | "vehicleMileage"
  | "vehicleModel"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const leasingvertragContactFormDefaultValues: DefaultValues<LeasingvertragContactFormDataT> =
  {
    company: undefined,
    salutation: undefined,
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    comments: undefined,
    tradeIn: false,
    licensePlate: "",
    make: undefined,
    contactPerson: undefined,
    makeName: undefined,
    vehicleLicensePlate: undefined,
    vehicleVin: undefined,
    vehicleMileage: undefined,
    vehicleModel: undefined,
    privacy: false,
    mail: "",
    case: "OPPORTUNITY",
    URL: undefined,
    source: "Leasingvertrag Anfrage",
    dealerdesk: "Leasingvertrag",
  };

const primaryFields: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT>[] =
  [
    {
      name: "company",
      type: "text",
      label: "Firma",
      placeholder: "Firma",
    },
    {
      name: "salutation",
      type: "select",
      label: "Anrede",
      placeholder: "Bitte auswählen...",
      required: true,
      options: [
        { value: "Herr", label: "Herr" },
        { value: "Frau", label: "Frau" },
        { value: "Divers", label: "Divers" },
      ],
    },
    {
      name: "firstname",
      type: "name",
      label: "Vorname",
      placeholder: "Vorname",
      required: true,
    },
    {
      name: "lastname",
      type: "name",
      label: "Name",
      placeholder: "Nachname",
      required: true,
    },
    {
      name: "email",
      type: "text",
      inputType: "email",
      label: "E-Mail",
      placeholder: "E-Mail",
      required: true,
    },
    {
      name: "phone",
      type: "phone",
      label: "Telefon",
      placeholder: "+49 (123) 45678902",
      required: true,
    },
  ];

// Additional fields for leasing contract
const leasingFields: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT>[] =
  [
    {
      name: "licensePlate",
      type: "text",
      label: "Kennzeichen",
      placeholder: "Ihr Kennzeichen",
      required: true,
      colSpan: 2,
    },
    {
      name: "make",
      type: "select",
      label: "Marke",
      placeholder: "Bitte auswählen...",
      required: true,
    },
    {
      name: "contactPerson",
      type: "select",
      label: "Ansprechpartner",
      placeholder: "Bitte auswählen...",
      required: true,
      disabled: (fieldValues) => !fieldValues.make || fieldValues.make === "",
    },
  ];

const commentsField: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT> =
  {
    name: "comments",
    type: "textarea",
    label: "Gibt es weitere Bemerkungen oder Hinweise?",
    placeholder: "Ihre Nachricht...",
    colSpan: 2,
  };

const privacyField: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT> =
  {
    name: "privacy",
    type: "checkbox",
    colSpan: 2,
    label: (
      <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
        Mit absenden bestätigen Sie unsere{" "}
        <Link href="/datenschutz" className="underline">
          Datenschutzerklärung
        </Link>
        .
      </span>
    ),
  };

const honeypotField: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT> =
  {
    name: "mail",
    type: "honeypot",
  };

const caseField: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT> =
  {
    name: "case",
    type: "select",
    hidden: true,
    options: [
      { value: "CASE", label: "CASE" },
      { value: "OPPORTUNITY", label: "OPPORTUNITY" },
    ],
  };

const urlField: ContactFormFieldConfigT<LeasingvertragContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

export const leasingvertragContactFormConfig: ContactFormConfigT<LeasingvertragContactFormFieldNameT> =
  {
    sections: [
      {
        id: "leasing",
        layout: "grid",
        columns: 2,
        fields: leasingFields,
      },
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: [...primaryFields, honeypotField, caseField, urlField],
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [commentsField, privacyField],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
